import { UploadOutlined } from "@ant-design/icons";
import type { MenuProps } from "antd";
import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import {
  BAvatar,
  BButton,
  BChatItem,
  BContent,
  BDocumentList,
  BDrawer,
  BDropdown,
  BEmpty,
  BHistoryList,
  BInput,
  BInputChat,
  BInputPassword,
  BLayout,
  BModal,
  BProjectList,
  BSider,
  BSpin,
  BTextArea,
  BUpload,
  type BChatItemStatus,
  type BHistoryListItem,
  type BProjectListItem,
} from "components/base";
import type { Document } from "types/document";
import ComponentCard from "./components/ComponentCard";
import ComponentSection from "./components/ComponentSection";
import "./ComponentsPage.scss";

type CatalogItem = {
  name: string;
  preview: ReactNode;
};

type CatalogSection = {
  title: string;
  items: CatalogItem[];
};

const dropdownItems: MenuProps["items"] = [
  { key: "1", label: "Tùy chọn 1" },
  { key: "2", label: "Tùy chọn 2" },
];

const historyDemoItems: BHistoryListItem[] = [
  { id: "1", title: "Công thức tính lãi/lỗ" },
  { id: "2", title: "Lỗi MobX Async" },
  { id: "3", title: "Lỗi phpMyAdmin UNIQUE colu..." },
  { id: "4", title: "React Quill Resize Ảnh" },
  { id: "5", title: "Cột NaN trong SQL" },
];

const projectDemoItems: BProjectListItem[] = [
  { id: "1", title: "Thiết kế lại website" },
  { id: "2", title: "Chiến dịch marketing" },
  { id: "3", title: "Cá nhân" },
  { id: "4", title: "Nghiên cứu" },
];

const documentDemoItems: Document[] = [
  {
    id: 1,
    createdAt: 0,
    updatedAt: 0,
    name: "Báo cáo tài chính Q1",
    fileName: "bao-cao-q1.pdf",
    fileSize: 0,
    ragDocumentId: "",
    chunksCreated: 0,
    mimeType: "application/pdf",
    filePath: "/demo/bao-cao-q1.pdf",
    sourceName: "",
    fileType: "PDF",
  },
  {
    id: 2,
    createdAt: 0,
    updatedAt: 0,
    name: "Hợp đồng dịch vụ",
    fileName: "hop-dong.docx",
    fileSize: 0,
    ragDocumentId: "",
    chunksCreated: 0,
    mimeType: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    filePath: "/demo/hop-dong.docx",
    sourceName: "",
    fileType: "DOCX",
  },
  {
    id: 3,
    createdAt: 0,
    updatedAt: 0,
    name: "Bảng chi phí marketing",
    fileName: "chi-phi.xlsx",
    fileSize: 0,
    ragDocumentId: "",
    chunksCreated: 0,
    mimeType: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    filePath: "/demo/chi-phi.xlsx",
    sourceName: "",
    fileType: "XLSX",
  },
];

const chatItemStaticAssistant = `Bạn có thể export thành file \`profitLoss.js\` như sau:

\`\`\`javascript
/** Tính % lãi/lỗ tạm tính */
export function calculateProfitLoss(buyPrice, currentPrice) {
  if (buyPrice == null || currentPrice == null) return 0;
  let percent = ((currentPrice - buyPrice) / buyPrice) * 100;
  return percent;
}
\`\`\``;

const chatItemStreamSample =
  "Đây là kết quả đúng theo công thức đã trao đổi trong nhóm. Bạn có thể dùng đoạn code mẫu phía trên để tính % lãi/lỗ tạm tính.";

const ComponentsPage = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [historyActiveId, setHistoryActiveId] = useState("1");
  const [historyCollapsed, setHistoryCollapsed] = useState(false);
  const [projectActiveId, setProjectActiveId] = useState("1");
  const [projectCollapsed, setProjectCollapsed] = useState(false);
  const [chatInputValue, setChatInputValue] = useState("");
  const [streamStatus, setStreamStatus] = useState<BChatItemStatus>("done");
  const [streamContent, setStreamContent] = useState(chatItemStreamSample);
  const streamTimersRef = useRef<{
    loading?: ReturnType<typeof setTimeout>;
    interval?: ReturnType<typeof setInterval>;
  }>({});

  const clearStreamTimers = useCallback(() => {
    if (streamTimersRef.current.loading) {
      clearTimeout(streamTimersRef.current.loading);
    }
    if (streamTimersRef.current.interval) {
      clearInterval(streamTimersRef.current.interval);
    }
    streamTimersRef.current = {};
  }, []);

  useEffect(() => () => clearStreamTimers(), [clearStreamTimers]);

  const handleChatAttach = useCallback((files: File[], type: string) => {
    console.log("BInputChat attach", type, files.map((file) => file.name));
  }, []);

  const handleChatMic = useCallback(() => {
    console.log("BInputChat mic");
  }, []);

  const handleChatSubmit = useCallback((text: string) => {
    console.log("BInputChat submit", text);
  }, []);

  const handleDemoStream = useCallback(() => {
    clearStreamTimers();
    setStreamContent("");
    setStreamStatus("loading");

    streamTimersRef.current.loading = setTimeout(() => {
      setStreamStatus("streaming");
      let index = 0;

      streamTimersRef.current.interval = setInterval(() => {
        index += 3;
        if (index >= chatItemStreamSample.length) {
          setStreamContent(chatItemStreamSample);
          setStreamStatus("done");
          clearStreamTimers();
          return;
        }
        setStreamContent(chatItemStreamSample.slice(0, index));
      }, 24);
    }, 600);
  }, [clearStreamTimers]);
  const sections: CatalogSection[] = [
    {
      title: "Chung",
      items: [
        {
          name: "BButton",
          preview: <BButton type="primary">Nút</BButton>,
        },
        {
          name: "BSpin",
          preview: <BSpin />,
        },
        {
          name: "BEmpty",
          preview: <BEmpty description="Không có dữ liệu" />,
        },
      ],
    },
    {
      title: "Nhập liệu",
      items: [
        {
          name: "BInput",
          preview: <BInput placeholder="BInput" style={{ width: "100%" }} />,
        },
        {
          name: "BInputPassword",
          preview: <BInputPassword placeholder="Mật khẩu" style={{ width: "100%" }} />,
        },
        {
          name: "BTextArea",
          preview: <BTextArea placeholder="BTextArea" rows={2} style={{ width: "100%" }} />,
        },
        {
          name: "BInputChat",
          preview: (
            <div className="components-page__input-chat-demo">
              <BInputChat
                value={chatInputValue}
                onChange={setChatInputValue}
                onAttach={handleChatAttach}
                onMicClick={handleChatMic}
                onSubmit={handleChatSubmit}
              />
            </div>
          ),
        },
        {
          name: "BUpload",
          preview: (
            <BUpload beforeUpload={() => false} showUploadList={false}>
              <BButton icon={<UploadOutlined />}>Tải lên</BButton>
            </BUpload>
          ),
        },
      ],
    },
    {
      title: "Bố cục",
      items: [
        {
          name: "BLayout",
          preview: (
            <BLayout className="components-page__layout-demo">
              <div className="components-page__layout-demo-bar">Đầu trang</div>
              <div className="components-page__layout-demo-body">Nội dung</div>
            </BLayout>
          ),
        },
        {
          name: "BSider",
          preview: (
            <BLayout className="components-page__layout-demo">
              <BSider width={56} className="components-page__layout-demo-sider">
                Bên
              </BSider>
              <div className="components-page__layout-demo-body">Chính</div>
            </BLayout>
          ),
        },
        {
          name: "BContent",
          preview: (
            <BLayout className="components-page__layout-demo">
              <BContent className="components-page__layout-demo-content">Nội dung</BContent>
            </BLayout>
          ),
        },
      ],
    },
    {
      title: "Điều hướng / Hiển thị",
      items: [
        {
          name: "BDropdown",
          preview: (
            <BDropdown menu={{ items: dropdownItems }}>
              <BButton>Menu thả xuống</BButton>
            </BDropdown>
          ),
        },
        {
          name: "BAvatar",
          preview: <BAvatar>BD</BAvatar>,
        },
        {
          name: "BHistoryList",
          preview: (
            <div className="components-page__history-demo">
              <BHistoryList
                items={historyDemoItems}
                activeId={historyActiveId}
                collapsed={historyCollapsed}
                onCollapsedChange={setHistoryCollapsed}
                onSelect={setHistoryActiveId}
              />
            </div>
          ),
        },
        {
          name: "BProjectList",
          preview: (
            <div className="components-page__project-demo">
              <BProjectList
                items={projectDemoItems}
                activeId={projectActiveId}
                collapsed={projectCollapsed}
                onCollapsedChange={setProjectCollapsed}
                onSelect={setProjectActiveId}
              />
            </div>
          ),
        },
        {
          name: "BDocumentList",
          preview: (
            <div className="components-page__document-demo">
              <BDocumentList items={documentDemoItems} />
            </div>
          ),
        },
        {
          name: "BChatItem",
          preview: (
            <div className="components-page__chat-item-demo">
              <BChatItem role="user" content="export cho tôi file js về logic này nhé" />
              <BChatItem role="assistant" content={chatItemStaticAssistant} status="done" />
              <div className="components-page__chat-item-demo-stream">
                <BButton type="primary" onClick={handleDemoStream}>
                  Xem stream
                </BButton>
                <BChatItem
                  role="user"
                  content="file typescript nữa nhé"
                />
                <BChatItem
                  role="assistant"
                  content={streamContent}
                  status={streamStatus}
                />
              </div>
            </div>
          ),
        },
      ],
    },
    {
      title: "Phản hồi",
      items: [
        {
          name: "BModal",
          preview: (
            <>
              <BButton type="primary" onClick={() => setModalOpen(true)}>
                Mở Modal
              </BButton>
              <BModal
                title="BModal"
                open={modalOpen}
                onOk={() => setModalOpen(false)}
                onCancel={() => setModalOpen(false)}
              >
                Nội dung demo Modal
              </BModal>
            </>
          ),
        },
        {
          name: "BDrawer",
          preview: (
            <>
              <BButton onClick={() => setDrawerOpen(true)}>Mở Drawer</BButton>
              <BDrawer title="BDrawer" open={drawerOpen} onClose={() => setDrawerOpen(false)}>
                Nội dung demo Drawer
              </BDrawer>
            </>
          ),
        },
      ],
    },
  ];

  return (
    <div className="components-page">
      <header className="components-page__header">
        <h1 className="components-page__title">Thành phần</h1>
        <p className="components-page__subtitle">
          Các wrapper cơ sở (`B*`) từ <code>src/components/base</code>
        </p>
      </header>

      {sections.map((section) => (
        <ComponentSection key={section.title} title={section.title} count={section.items.length}>
          {section.items.map((item) => (
            <ComponentCard key={item.name} title={item.name}>
              {item.preview}
            </ComponentCard>
          ))}
        </ComponentSection>
      ))}
    </div>
  );
};

export default ComponentsPage;
